import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

export type PracticeViewMode = 'structured' | 'priority';

export const PRACTICE_VIEW_OPTIONS = [
  { id: 'structured', label: 'Structured' },
  { id: 'priority', label: 'Practice Priority' },
] as const;

interface PracticeViewContextValue {
  viewMode: PracticeViewMode;
  setViewMode: (mode: PracticeViewMode) => void;
  showDescriptions: boolean;
  setShowDescriptions: (value: boolean) => void;
  groupByTopic: boolean;
  setGroupByTopic: (value: boolean) => void;
}

const PracticeViewContext = createContext<PracticeViewContextValue | null>(null);

interface PracticeViewProviderProps {
  defaultMode?: PracticeViewMode;
  defaultShowDescriptions?: boolean;
  defaultGroupByTopic?: boolean;
}

export const PracticeViewProvider = ({
  children,
  defaultMode = 'structured',
  defaultShowDescriptions = true,
  defaultGroupByTopic = true,
}: PropsWithChildren<PracticeViewProviderProps>) => {
  const [viewMode, setViewMode] = useState<PracticeViewMode>(defaultMode);
  const [showDescriptions, setShowDescriptions] = useState(defaultShowDescriptions);
  const [groupByTopic, setGroupByTopic] = useState(defaultGroupByTopic);
  const value = useMemo(
    () => ({
      viewMode,
      setViewMode,
      showDescriptions,
      setShowDescriptions,
      groupByTopic,
      setGroupByTopic,
    }),
    [groupByTopic, showDescriptions, viewMode]
  );

  return <PracticeViewContext.Provider value={value}>{children}</PracticeViewContext.Provider>;
};

export const usePracticeView = () => {
  const context = useContext(PracticeViewContext);

  if (!context) {
    throw new Error('usePracticeView must be used within <PracticeViewProvider />');
  }

  return context;
};
